/**
 * Where the public site finds its API.
 *
 * The SSR server injects the API endpoint as a container env var and serves it
 * to the browser at /config.json, but the site can find its API without one:
 * CloudFront routes `/api/*` to the API origin (see the web stack's
 * distribution behaviours) and vite proxies `/api` to the local server in dev.
 * So a relative base is correct on every surface, and an
 * unreadable config (missing file, malformed JSON) costs a 404
 * at worst rather than disabling a feature.
 *
 * Every caller — route loaders and components alike — resolves through here, so
 * there is one answer to "which API?" on the whole site.
 */

interface RuntimeConfig {
  api?: {
    endpoint?: string;
  };
}

/** Same-origin API base — the fallback when no runtime config names an endpoint. */
export const SAME_ORIGIN_API = "";

/** Join an endpoint base and an API path without doubling the slash. */
export function apiUrl(endpoint: string, path: string): string {
  return `${endpoint.replace(/\/+$/, "")}${path}`;
}

/**
 * The API base this deployment should use: whatever config.json names, else the
 * same origin. `baseUrl` lets a route loader resolve config.json against the
 * request it is handling rather than the document.
 *
 * Server (SSR): the container's API_ENDPOINT env var, else the origin of the
 * request being handled — the dev server proxies /api, and a same-origin base
 * is right wherever the deployment's CDN routes /api/* to the API.
 */
export async function resolveApiEndpoint(options?: {
  baseUrl?: string;
  signal?: AbortSignal;
}): Promise<string> {
  if (typeof window === "undefined") {
    return (
      process.env.API_ENDPOINT ??
      (options?.baseUrl ? new URL(options.baseUrl).origin : SAME_ORIGIN_API)
    );
  }
  try {
    const configUrl = options?.baseUrl
      ? new URL("/config.json", options.baseUrl)
      : "/config.json";
    const response = await fetch(configUrl, { signal: options?.signal });
    if (!response.ok) return SAME_ORIGIN_API;
    const config = (await response.json()) as RuntimeConfig;
    // A config naming a non-string endpoint is as unusable as no config at all
    // — fall back rather than handing junk to apiUrl().
    return typeof config?.api?.endpoint === "string"
      ? config.api.endpoint
      : SAME_ORIGIN_API;
  } catch {
    return SAME_ORIGIN_API;
  }
}
